import type {
  BiologicalSex,
  PatientMeasurementField,
  PatientMeasurementMetricType,
} from '../types';
import { differenceInCalendarDays, startOfDay } from 'date-fns';

export const formatMetricValue = (
  value: number | null,
  options?: { decimals?: number }
) => {
  if (value == null || !Number.isFinite(value)) {
    return '-';
  }

  return value.toFixed(options?.decimals ?? 2);
};

export const formatMeasurementValue = (
  value: number | null,
  unit: string,
  decimals: number = 1
) => {
  if (value == null || !Number.isFinite(value)) {
    return '-';
  }

  return `${value.toFixed(decimals)} ${unit}`;
};

export const formatBiologicalSex = (value: BiologicalSex) => {
  if (value === 'male') return 'Maschio';
  if (value === 'female') return 'Femmina';
  return '-';
};

export const formatDateValue = (value: string | null | undefined) => {
  if (value == null || value.trim() === '') {
    return '-';
  }

  const trimmed = value.trim();
  const yearOnly = trimmed.match(/^(\d{4})$/);
  if (yearOnly != null) {
    return yearOnly[1];
  }

  // Year-only birth is stored as YYYY-01-01 for DATE columns.
  const spannometric = trimmed.match(/^(\d{4})-01-01/);
  if (spannometric != null) {
    return spannometric[1];
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('it-IT');
};

export const formatBirthYearValue = (value: string | null | undefined) => {
  if (value == null || value.trim() === '') {
    return '-';
  }

  const trimmed = value.trim();
  const yearOnly = trimmed.match(/^(\d{4})$/);
  if (yearOnly != null) {
    return yearOnly[1];
  }

  const fullDate = trimmed.match(/^(\d{4})-/);
  if (fullDate != null) {
    return fullDate[1];
  }

  return '-';
};

export const formatDaysAgoLabel = (value: string | null | undefined) => {
  if (value == null || value.trim() === '') {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const days = differenceInCalendarDays(
    startOfDay(new Date()),
    startOfDay(date)
  );

  if (days < 0) {
    return null;
  }

  if (days === 0) {
    return 'oggi';
  }

  if (days === 1) {
    return '1 giorno fa';
  }

  return `${days} giorni fa`;
};

export const formatDateTimeValue = (value: string | null | undefined) => {
  if (value == null || value.trim() === '') {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('it-IT');
};

export const formatAgeValue = (age: number | null) => {
  if (age == null) {
    return '-';
  }

  return `~${age} anni`;
};

export const formatMeasurementField = (field: PatientMeasurementField) => {
  if (field.value.trim() === '') {
    return '-';
  }

  return field.unit != null && field.unit !== ''
    ? `${field.value} ${field.unit}`
    : field.value;
};

export const formatMetricTypeLabel = (
  metricType: PatientMeasurementMetricType
) => {
  switch (metricType) {
    case 'weight_kg':
      return 'Peso (calcoli)';
    case 'shoulder_cm':
      return 'Spalle (calcoli)';
    case 'hips_cm':
      return 'Fianchi (calcoli)';
    case 'waist_cm':
      return 'Vita (calcoli)';
    case 'chest_cm':
      return 'Petto (calcoli)';
    case 'neck_cm':
      return 'Collo (calcoli)';
    case 'thigh_cm':
      return 'Coscia (calcoli)';
    case 'calf_cm':
      return 'Polpacci (calcoli)';
    default:
      return 'Nessuno';
  }
};
